import { ArrowUpRight } from "lucide-react";
import CardServicos from "./cardServicos";
import AccordionServicos from "./accordionServicos";
import FormularioRD from "../rdstation/formularioRD";
import { useModal } from "../contexts/ModalContext";
import OtimizeSeuNegocio from "./OtimizeSeuNegocio";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "./carousel";
import { useState, useEffect } from "react";

// Custom NumberedCard component for when no icon is provided
const NumberedCard = ({ number, title, text, glowColor = "#A002CB", url, className = "" }) => {
    const [isHovered, setIsHovered] = useState(false);

    function handleMouseEnter() {
        setIsHovered(true);
    }

    function handleMouseLeave() {
        setIsHovered(false);
    }

    const CardContent = () => {
        return (
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleMouseEnter}
                onTouchEnd={handleMouseLeave}
                className={`relative mb-4 md:mb-0 hover:cursor-pointer ${className}`}
            >
                <div
                    className={`absolute inset-0 transition-opacity duration-300 blur-xl ${isHovered ? "opacity-80" : "opacity-0"}`}
                    style={{ backgroundColor: glowColor }}
                />
                <div className="relative z-10 h-[350px] w-[350px] rounded-lg p-8 bg-[#1B182D] text-white">
                    <div className="relative w-[58px] h-[58px]">
                        <div
                            className={`absolute inset-0 rounded-full transition-all duration-300 ${isHovered ? "bg-gradient-to-r from-[#4B3BDB] via-[#A002CB] to-[#FC0021] opacity-100" : "bg-white/10 blur-[2px]"}`}
                        />
                        <div className="absolute inset-[2px] rounded-full bg-[#1B182D] flex items-center justify-center">
                            <span className="text-white font-poppins-semibold text-xl">{number}</span>
                        </div>
                    </div>
                    <h1 className="mt-6 font-inter-bold text-2xl">{title}</h1>
                    <p className="mt-6 font-inter-regular text-base opacity-80">{text}</p>
                </div>
            </div>
        );
    };

    return url ? (
        <a href={url} className="block no-underline text-inherit">
            <CardContent />
        </a>
    ) : (
        <CardContent />
    );
};

export default function Diferenciais({ servicosData, title, showOtimize = true }) {
    const { openModal } = useModal();
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [expandedMobileIndex, setExpandedMobileIndex] = useState(null);

    // Track which accordion is open on mobile
    const handleAccordionToggle = (index, isOpen) => {
        if (isOpen) {
            // Close any previously opened accordion
            if (expandedMobileIndex !== null && expandedMobileIndex !== index) {
                // Find the previously opened accordion and close it
                const prevAccordion = document.querySelector(`[data-accordion-index="${expandedMobileIndex}"]`);
                if (prevAccordion) {
                    prevAccordion.click();
                }
            }
            setExpandedMobileIndex(index);
        } else if (expandedMobileIndex === index) {
            setExpandedMobileIndex(null);
        }
    };

    return (
        <div className="w-full bg-[url(../assets/cover-proposito.png)] py-12 md:py-20 relative overflow-hidden text-white">
            <div className="max-w-[1200px] mx-auto relative z-10 px-4 md:px-8">
                <h2 className="font-inter-regular text-3xl md:text-[60px] text-center mb-12">
                    {title}
                </h2>
                {/* Mobile Accordion View */}
                <div className="md:hidden space-y-4 mb-8">
                    {servicosData.map((servico, index) => (
                        <div key={`mobile-container-${index}`} className="relative">
                            <AccordionServicos
                                key={`mobile-${index}`}
                                {...servico}
                                title={servico.title || `Diferencial ${index + 1}`}
                                doesThereButton={false}
                                onToggle={(isOpen) => handleAccordionToggle(index, isOpen)}
                                accordionIndex={index}
                            />
                            
                            {/* Mobile OtimizeSeuNegocio that appears when accordion is expanded */}
                            {showOtimize && (
                                <div 
                                    className={`transition-all duration-300 ease-in-out transform w-full overflow-hidden
                                        ${expandedMobileIndex === index ? 'max-h-[100px] opacity-100' : 'max-h-0 opacity-0'}`}
                                    style={{
                                        transitionProperty: 'max-height, opacity, transform',
                                        transform: expandedMobileIndex === index ? 'translateY(0)' : 'translateY(-10px)',
                                        zIndex: 20,
                                        position: 'relative'
                                    }}
                                >
                                    <OtimizeSeuNegocio 
                                        text="Otimize seu negócio!"
                                        buttonText="Contratar"
                                        useFormularioRD={true}
                                        type="solid"
                                        className="w-full mt-4"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {/* Desktop Carousel View */}
                <div className="relative mx-[-1rem] md:mx-[-2rem] hidden md:block">
                    <Carousel className="overflow-visible">
                        <div className="flex justify-end absolute right-8 md:right-16 top-6 z-10">
                            <CarouselPrevious className="h-8 w-8 rounded-full bg-transparent hover:cursor-pointer hover:scale-130 transition-transform duration-300" />
                            <CarouselNext className="h-8 w-8 rounded-full bg-transparent hover:cursor-pointer hover:scale-130 transition-transform duration-300" />
                        </div>
                        <CarouselContent className="py-8 px-6 md:px-8">
                            {servicosData.map((servico, index) => (
                                <CarouselItem
                                    key={index}
                                    className="basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/3 pl-4 md:pl-6"
                                >
                                    <div className="relative overflow-visible px-2">
                                        {/* Card container with fixed height to maintain alignment */}
                                        <div 
                                            className="relative h-[350px] mb-20" 
                                            onMouseEnter={() => setHoveredIndex(index)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                        >
                                            {servico.icon ? (
                                                <CardServicos 
                                                    {...servico} 
                                                    height={350}
                                                    className="hidden md:block"
                                                />
                                            ) : (
                                                <NumberedCard number={index + 1} {...servico} className="hidden md:block" />
                                            )}
                                            
                                            {/* OtimizeSeuNegocio component that appears on hover */}
                                            {showOtimize && (
                                                <div 
                                                    className={`hidden md:block absolute left-0 right-0 bottom-[-120px] transition-all duration-300 ${hoveredIndex === index ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                                                >
                                                    <OtimizeSeuNegocio 
                                                        text="Otimize seu negócio!"
                                                        buttonText="Contratar"
                                                        useFormularioRD={true}
                                                        type="solid"
                                                        className="w-full"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </div>
            </div>
        </div>
    );
}
